/**
 * Workflow types (SPEC PX-4 — workflow-builder-2026-06-17).
 *
 * Mirrors the engine's stored `workflow_definition` shape
 * (`services/workflow_engine.py`): a FLAT `actions[]` array with config keys at
 * the TOP LEVEL of each action dict, all control flow as INTEGER
 * `target_action_index` values into that same array. These types are the wire
 * contract for the existing `/api/workflows/*` endpoints + the new
 * read-only `/api/workflows/metadata` palette descriptor.
 */

/** A single action in the engine's flat `actions[]` array. Config keys are FLAT
 * on the dict — NEVER nested under a `config` key (nesting would not parse). */
export interface WorkflowActionDict {
  type: string;
  // Flat config keys live directly on the action dict (delay_minutes, channel,
  // content, branches, target indices, …). Open map — the metadata schema drives
  // which keys are valid per type.
  [key: string]: unknown;
}

export interface WorkflowTrigger {
  type: string;
  config?: Record<string, unknown>;
  // Convenience top-level trigger keys (tag, to_stage, …) are also accepted by
  // the engine; the canvas emits them under `config`.
  [key: string]: unknown;
}

export interface ReEnrollment {
  policy: string; // once | always | if_completed | after_N_days
}

export interface GoalCondition {
  trigger_type?: string;
  condition?: Record<string, unknown>;
}

/** A stored workflow definition (the GET /{id} shape + list-row shape). */
export interface WorkflowDefinition {
  id: string;
  workflow_id?: string;
  tenant_id?: string;
  name: string;
  active: boolean;
  trigger: WorkflowTrigger;
  actions: WorkflowActionDict[];
  re_enrollment?: ReEnrollment;
  goal_condition?: GoalCondition | null;
  created_at?: string;
  updated_at?: string;
}

export interface WorkflowListItem extends WorkflowDefinition {}

/** Create body — `active` always false on first create (dead-by-default). */
export interface WorkflowCreate {
  name: string;
  trigger: WorkflowTrigger;
  actions: WorkflowActionDict[];
  active: false;
  re_enrollment?: ReEnrollment;
  goal_condition?: GoalCondition | null;
}

/** Partial update body (also the activate path via `{active}`). */
export interface WorkflowUpdate {
  name?: string;
  trigger?: WorkflowTrigger;
  actions?: WorkflowActionDict[];
  active?: boolean;
  re_enrollment?: ReEnrollment;
  goal_condition?: GoalCondition | null;
}

// --- /api/workflows/metadata descriptor ------------------------------------

/** A JSON-schema property descriptor (subset of model_json_schema output). */
export interface SchemaProperty {
  type?: string | string[];
  default?: unknown;
  const?: unknown;
  enum?: unknown[];
  title?: string;
  description?: string;
  anyOf?: SchemaProperty[];
  items?: SchemaProperty;
  [key: string]: unknown;
}

export interface ActionSchema {
  title?: string;
  type?: string;
  properties?: Record<string, SchemaProperty>;
  required?: string[];
  [key: string]: unknown;
}

export interface MetadataTrigger {
  type: string;
  config_fields: string[];
}

export interface MetadataAction {
  type: string;
  schema: ActionSchema;
}

export interface WorkflowMetadata {
  triggers: MetadataTrigger[];
  actions: MetadataAction[];
}

// --- Canvas node/edge model (the decompiled graph) -------------------------

export type NodeKind = 'trigger' | 'action';

/** A node in the canvas graph. The trigger node carries `trigger.type` + config;
 * an action node carries its `type` + the flat config fields. */
export interface GraphNode {
  /** Stable node id (used for edges + React keys). The trigger node is `trigger`. */
  id: string;
  kind: NodeKind;
  /** trigger.type for the trigger node; action `type` for action nodes. */
  type: string;
  /** Flat config (action fields, or trigger config merged). Never nested. */
  config: Record<string, unknown>;
  /** Layout position (assigned by the layout pass; drag updates it in R2). */
  x: number;
  y: number;
}

export type EdgeKind =
  | 'linear'
  | 'branch'
  | 'branch_default'
  | 'split'
  | 'event'
  | 'timeout';

/** A directed edge between two nodes (or to a terminal marker). */
export interface GraphEdge {
  id: string;
  source: string;
  /** Target node id, or `__end__` for an out-of-bounds / terminal target. */
  target: string;
  kind: EdgeKind;
  /** Human-readable label (branch condition summary, split weight, event/timeout). */
  label?: string;
}

export interface WorkflowGraph {
  nodes: GraphNode[];
  edges: GraphEdge[];
  name: string;
  trigger: WorkflowTrigger;
  re_enrollment: ReEnrollment;
  goal_condition?: GoalCondition | null;
}

/** Sentinel target id for an out-of-bounds / terminal jump (render as an end cap). */
export const TERMINAL_NODE_ID = '__end__';
export const TRIGGER_NODE_ID = 'trigger';
